import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button, buttonVariants } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import {
  FolderKanban,
  Users,
  CheckSquare,
  TrendingUp,
  AlertCircle,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import type { ApiResponse, Project, Lead } from '@/types';

interface DashboardStats {
  activeProjects: number;
  openLeads: number;
  tasksDue: number;
  revenueYTD: number;
}

export function DashboardPage(): React.ReactElement {
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async (): Promise<DashboardStats> => {
      // Aggregate stats from multiple endpoints
      const [projectsRes, leadsRes] = await Promise.all([
        api.get<Project[]>('/projects'),
        api.get<Lead[]>('/leads'),
      ]);

      const projects = projectsRes.success ? (projectsRes.data ?? []) : [];
      const leads = leadsRes.success ? (leadsRes.data ?? []) : [];

      return {
        activeProjects: projects.filter(p => p.status === 'active').length,
        openLeads: leads.filter(l => !['won', 'lost'].includes(l.stage)).length,
        tasksDue: 0, // Tasks endpoint not yet built
        revenueYTD: projects.reduce((sum, p) => sum + (p.budgetCents ?? 0), 0),
      };
    },
  });

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['dashboard-projects'],
    queryFn: async (): Promise<Project[]> => {
      const res: ApiResponse<Project[]> = await api.get('/projects', { limit: 5 });
      return res.success ? (res.data ?? []) : [];
    },
  });

  const stats = statsData ?? { activeProjects: 0, openLeads: 0, tasksDue: 0, revenueYTD: 0 };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Your construction business at a glance</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-2 border-l-primary">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Projects</p>
                {statsLoading ? (
                  <Skeleton className="mt-1 h-8 w-16" />
                ) : (
                  <p className="text-3xl font-bold">{stats.activeProjects}</p>
                )}
              </div>
              <FolderKanban className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-2 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Leads</p>
                {statsLoading ? (
                  <Skeleton className="mt-1 h-8 w-16" />
                ) : (
                  <p className="text-3xl font-bold">{stats.openLeads}</p>
                )}
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-2 border-l-amber-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasks Due</p>
                {statsLoading ? (
                  <Skeleton className="mt-1 h-8 w-16" />
                ) : (
                  <p className="text-3xl font-bold">{stats.tasksDue}</p>
                )}
              </div>
              <CheckSquare className="h-8 w-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-2 border-l-emerald-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Revenue YTD</p>
                {statsLoading ? (
                  <Skeleton className="mt-1 h-8 w-24" />
                ) : (
                  <p className="text-3xl font-bold">
                    {formatCurrency(stats.revenueYTD)}
                  </p>
                )}
              </div>
              <TrendingUp className="h-8 w-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent Projects */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recent Projects</CardTitle>
              <Link to="/projects" className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}>
                View all <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </CardHeader>
            <CardContent>
              {projectsLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : (projects?.length ?? 0) === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <FolderKanban className="mx-auto mb-2 h-8 w-8 opacity-50" />
                  <p>No projects yet</p>
                  <Button variant="link" size="sm" asChild>
                    <Link to="/projects">Create your first project</Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {projects?.slice(0, 5).map((project) => (
                    <Link
                      key={project.id}
                      to="/projects"
                      className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{project.name}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {project.siteAddress ?? 'No address'}
                        </p>
                      </div>
                      <div className="ml-4 flex items-center gap-3">
                        <Badge
                          variant={
                            project.status === 'active' ? 'default' :
                            project.status === 'completed' ? 'secondary' :
                            project.status === 'on_hold' ? 'destructive' : 'outline'
                          }
                        >
                          {project.status}
                        </Badge>
                        {project.budgetCents != null && (
                          <span className="text-sm font-medium">
                            {formatCurrency(project.budgetCents)}
                          </span>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* AI Insights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <AlertCircle className="h-4 w-4 text-primary" />
                AI Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-lg border border-border p-4">
                  <p className="text-sm font-medium">AI Estimation Available</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Upload floor plans for instant AI-powered takeoffs and cost estimates.
                  </p>
                  <Button variant="link" size="sm" className="mt-2 h-auto p-0" asChild>
                    <Link to="/estimates">Try AI Estimation →</Link>
                  </Button>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-sm font-medium">Document Intelligence</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    AI will auto-categorize, summarize, and extract data from your uploaded documents.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Tasks */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Pending Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center py-4 text-center text-muted-foreground">
                <Calendar className="mb-2 h-8 w-8 opacity-50" />
                <p className="text-sm">No pending tasks</p>
                <p className="text-xs">Tasks from active projects will appear here</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
